import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import withRouter from "../Components/Common/withRouter";

//import Components
import RightSidebar from "../Components/Common/RightSidebar";
import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";

//import actions
import {
  changeLayout,
  changeLayoutMode,
  changeLayoutPosition,
  changeLayoutWidth,
  changeLeftsidebarSizeType,
  changeLeftsidebarViewType,
  changeSidebarImageType,
  changeSidebarTheme,
  changeSidebarVisibility,
  changeTopbarTheme,
} from "../slices/thunks";

//redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

// ✅ MOVE SELECTOR OUTSIDE COMPONENT - This is the KEY FIX
const selectLayoutState = state => state.Layout;

const selectLayoutProperties = createSelector(selectLayoutState, layout => ({
  layoutType: layout.layoutType,
  leftSidebarType: layout.leftSidebarType,
  layoutModeType: layout.layoutModeType,
  layoutWidthType: layout.layoutWidthType,
  layoutPositionType: layout.layoutPositionType,
  topbarThemeType: layout.topbarThemeType,
  leftsidbarSizeType: layout.leftsidbarSizeType,
  leftSidebarViewType: layout.leftSidebarViewType,
  leftSidebarImageType: layout.leftSidebarImageType,
  preloader: layout.preloader,
  sidebarVisibilitytype: layout.sidebarVisibilitytype,
}));

const Layout = props => {
  const [headerClass, setHeaderClass] = useState("");
  const dispatch = useDispatch();

  // ✅ SELECTOR ALREADY MEMOIZED FROM OUTSIDE
  const {
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    preloader,
    sidebarVisibilitytype,
  } = useSelector(selectLayoutProperties);

  /*
    layout settings
    */
  useEffect(() => {
    if (
      layoutType ||
      leftSidebarType ||
      layoutModeType ||
      layoutWidthType ||
      layoutPositionType ||
      topbarThemeType ||
      leftsidbarSizeType ||
      leftSidebarViewType ||
      leftSidebarImageType ||
      sidebarVisibilitytype
    ) {
      window.dispatchEvent(new Event("resize"));
      dispatch(changeLeftsidebarViewType(leftSidebarViewType));
      dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
      dispatch(changeSidebarTheme(leftSidebarType));
      dispatch(changeLayoutMode(layoutModeType));
      dispatch(changeLayoutWidth(layoutWidthType));
      dispatch(changeLayoutPosition(layoutPositionType));
      dispatch(changeTopbarTheme(topbarThemeType));
      dispatch(changeLayout(layoutType));
      dispatch(changeSidebarImageType(leftSidebarImageType));
      dispatch(changeSidebarVisibility(sidebarVisibilitytype));
    }
  }, [
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
    dispatch,
  ]);

  /*
    call dark/light mode
    */
  const onChangeLayoutMode = value => {
    if (changeLayoutMode) {
      dispatch(changeLayoutMode(value));
    }
  };

  // ✅ FIX: Add/remove scroll listener properly with cleanup
  useEffect(() => {
    const scrollNavigation = () => {
      var scrollup = document.documentElement.scrollTop;
      if (scrollup > 50) {
        setHeaderClass("topbar-shadow");
      } else {
        setHeaderClass("");
      }
    };

    window.addEventListener("scroll", scrollNavigation, true);

    return () => {
      window.removeEventListener("scroll", scrollNavigation, true);
    };
  }, []);

  // ✅ FIX: Hamburger icon toggle with proper cleanup
  useEffect(() => {
    const hamburgerIcon = document.querySelector(".hamburger-icon");

    if (
      sidebarVisibilitytype === "show" ||
      layoutType === "vertical" ||
      layoutType === "twocolumn"
    ) {
      hamburgerIcon?.classList.remove("open");
    } else {
      hamburgerIcon?.classList.add("open");
    }
  }, [sidebarVisibilitytype, layoutType]);

  // --- Clear Session Storage on Module Switching ---
  useEffect(() => {
    const currentPath = props.router.location.pathname;

    // 1. Projects State (Keep if in Project detail or list)
    const isProjectModule =
      currentPath.startsWith("/projects") ||
      currentPath.startsWith("/add-project") ||
      currentPath.startsWith("/project/view/");

    if (!isProjectModule) {
      sessionStorage.removeItem("projectTableState");
    }

    // 2. Inquiries State (Keep if in Inquiry detail, form, or list)
    const isInquiryModule =
      currentPath.startsWith("/inquiries") ||
      currentPath.startsWith("/add-inquirie") ||
      currentPath.startsWith("/edit-inquirie") ||
      currentPath.startsWith("/inquiries/details-inquiry") ||
      currentPath.startsWith("/inquiries/excel-upload");

    if (!isInquiryModule) {
      sessionStorage.removeItem("inquiryTableState");
    }

    // 3. Project Visit Report State (Keep only while on the report page)
    const isProjectVisitReport = currentPath.startsWith("/dashboard/reports/project-visit");

    if (!isProjectVisitReport) {
      sessionStorage.removeItem("projectVisitReportState");
    }

    // 4. Roles State (Master)
    if (!currentPath.startsWith("/master/roles")) {
      sessionStorage.removeItem("rolesTableState");
    }

    // 5. User State (Master)
    if (!currentPath.startsWith("/master/user")) {
      sessionStorage.removeItem("userTableState");
    }

    // 6. Employee State (Master)
    if (!currentPath.startsWith("/master/employee")) {
      sessionStorage.removeItem("employeeTableState");
    }

    // 7. Bank State (Master)
    if (!currentPath.startsWith("/master/banks")) {
      sessionStorage.removeItem("bankTableState");
    }

    // 8. Developer State
    const isDeveloperModule =
      currentPath.startsWith("/developer") ||
      currentPath.startsWith("/add-developer") ||
      currentPath.startsWith("/developer/view");

    if (!isDeveloperModule) {
      sessionStorage.removeItem("developerTableState");
    }
  }, [props.router.location.pathname]);

  return (
    <React.Fragment>
      <div id="layout-wrapper">
        <Header
          headerClass={headerClass}
          layoutModeType={layoutModeType}
          onChangeLayoutMode={onChangeLayoutMode}
        />
        <Sidebar layoutType={layoutType} />
        <div className="main-content">
          {props.children}
          {/* <Footer /> */}
        </div>
      </div>
      <RightSidebar />
    </React.Fragment>
  );
};

Layout.propTypes = {
  children: PropTypes.object,
};

export default withRouter(Layout);
